import {
  afterWheelLedgerChange,
  addRecommendationPosition,
  countPositionTransactions,
  deleteConfirmMessage,
  deleteOpenPosition,
  etTodayISO,
  prepareExpDateField,
  readExpDateField,
  findOpeningTransaction,
  positionSummary,
  resolveOpenPosition,
  updateOpeningTransaction,
  WheelActionError,
  type PositionResolution,
} from "./wheelActions";
import { confirmDialog } from "./confirmDialog";
import { isToastChipNode, showToast } from "./toast";
import {
  CUSTOM_PANEL_MARKER_ATTR,
  NATIVE_PANEL_ATTR,
  showPanel,
} from "./tabs";
import { fmtProfitPct, recommendationProfitability } from "./profitability";
import {
  esc,
  fmtEarningsDate,
  fmtNum,
  fmtUsd,
  scoreForTicker,
  stockForTicker,
  type OptionPosition,
  type WheelRecommendation,
} from "./watchlistData";
import {
  isWheelTabTickerFilterActive,
  toggleWheelTabTickerFilter,
  type WheelTickerFilterTarget,
  wheelTargetForRecSide,
} from "./wheelTickerFilterFromMenu";
import { renderRowFilterMenuItem } from "./rowFilterMenuItem";
import {
  ROBINHOOD_WINDOW_NAME,
  consumeRobinhoodMenuClick,
  openRobinhoodNearbyTab,
  prefetchRobinhoodPage,
  robinhoodHrefForIntent,
  robinhoodIntentFromPosition,
  robinhoodIntentFromRec,
  resolveRobinhoodHref,
} from "./robinhoodOpen";
import { openLegendForContext, type LegendOpenContext } from "./legendOpen";
import { isStockForgeDesktop } from "./desktopShell";
import { isRobinhoodBrokerConnected } from "./robinhoodStatus";

type RefreshFn = () => void | Promise<void>;

let refreshFn: RefreshFn | null = null;
let rowMenuDocBound = false;
let openMenuAnchor: HTMLElement | null = null;
let openMenuRestore: FocusMenuRestore | null = null;
let menuSuppressCloseUntil = 0;
let menuOpenedAt = 0;
const MENU_GESTURE_MS = 400;
let menuKeepAliveObserver: MutationObserver | null = null;
let actionChipSuppressUntil = 0;
let actionChipEditingUntil = 0;
let actionChipOpen = false;
let actionChipAnchor: HTMLElement | null = null;
let actionChipKeepAliveObserver: MutationObserver | null = null;
let focusActionChipEscapeBound = false;

const FOCUS_MENU_PORTAL_ID = "sfe-focus-menu-portal";
const FOCUS_ACTION_CHIP_ID = "sfe-focus-action-chip";

interface FocusMenuRestore {
  ticker: string;
  menuKind: "position" | "rec" | "stock" | "history";
  positionSymbol?: string;
  recKey?: string;
  txnId?: number;
}

export const STOCK_HISTORY_OPEN_EVENT = "sfe-stock-history-open";

export type HistoryTxnMenuAction =
  | "add"
  | "edit"
  | "delete"
  | "toggle-linked"
  | "link-wheel-pair"
  | "change-account";

export interface HistoryRowMenuPayload {
  txnId: number;
  linked?: boolean;
  linkedExpanded?: boolean;
  /** Assigned call — show wheel-pair link to put ASGN. */
  callAssign?: boolean;
  /** Own-ledger History — allow changing broker / account. */
  canReassignAccount?: boolean;
}

export const HISTORY_TXN_MENU_EVENT = "sfe-history-txn-menu";

const FOCUS_FILTER_MENU_ITEM = renderRowFilterMenuItem({
  active: false,
  actionAttr: "data-action",
  actionValue: "focus-wheel-filter",
});

function openRobinhoodMenuItem(): string {
  if (!isRobinhoodBrokerConnected()) return "";
  return `<a class="sfe-focus-menu-item sfe-focus-menu-item-robinhood" role="menuitem" data-action="focus-open-robinhood" target="${ROBINHOOD_WINDOW_NAME}" rel="opener">Open in Robinhood</a>`;
}

function openLegendMenuItem(): string {
  if (!isRobinhoodBrokerConnected()) return "";
  return `<button type="button" class="sfe-focus-menu-item sfe-focus-menu-item-legend" role="menuitem" data-action="focus-open-legend">Open in Legend</button>`;
}

function joinMenuItems(...items: string[]): string {
  return items.filter(Boolean).join("");
}

function positionMenuHtml(): string {
  return joinMenuItems(
    FOCUS_FILTER_MENU_ITEM,
    openRobinhoodMenuItem(),
    openLegendMenuItem(),
    `<button type="button" class="sfe-focus-menu-item" role="menuitem" data-action="focus-pos-add">Add</button>`,
    `<button type="button" class="sfe-focus-menu-item" role="menuitem" data-action="focus-pos-edit">Edit</button>`,
    `<button type="button" class="sfe-focus-menu-item" role="menuitem" data-action="focus-pos-close">Close</button>`,
    `<button type="button" class="sfe-focus-menu-item" role="menuitem" data-action="focus-pos-expired">Expired</button>`,
    `<button type="button" class="sfe-focus-menu-item" role="menuitem" data-action="focus-pos-assigned">Assigned</button>`,
    `<button type="button" class="sfe-focus-menu-item sfe-focus-menu-item-danger" role="menuitem" data-action="focus-pos-delete">Delete…</button>`,
  );
}

function recMenuHtml(): string {
  return joinMenuItems(
    FOCUS_FILTER_MENU_ITEM,
    openRobinhoodMenuItem(),
    openLegendMenuItem(),
    `<button type="button" class="sfe-focus-menu-item" role="menuitem" data-action="focus-rec-add">Add</button>`,
  );
}

function stockMenuHtml(): string {
  return joinMenuItems(
    FOCUS_FILTER_MENU_ITEM,
    openRobinhoodMenuItem(),
    openLegendMenuItem(),
    `<button type="button" class="sfe-focus-menu-item" role="menuitem" data-action="focus-stock-history">History</button>`,
  );
}

const RESOLVE_META: Record<
  PositionResolution,
  { title: string; submit: string; hint: string; showPrice: boolean }
> = {
  closed: {
    title: "Close position",
    submit: "Record close",
    hint: "Records a buy-to-close. Enter the premium you paid per share.",
    showPrice: true,
  },
  expired: {
    title: "Mark expired",
    submit: "Record expiry",
    hint: "Records the option expiring worthless at $0 — you keep the full premium.",
    showPrice: false,
  },
  assigned: {
    title: "Mark assigned",
    submit: "Record assignment",
    hint: "Records assignment at the strike price (100 shares per contract).",
    showPrice: false,
  },
};

export function setFocusActionsRefresh(fn: RefreshFn | null): void {
  refreshFn = fn;
}

function actionErr(err: unknown): void {
  showToast(err instanceof WheelActionError ? err.message : "Something went wrong.", "error");
}

function prepareActionChip(chip: HTMLElement): void {
  if (chip.hasAttribute(NATIVE_PANEL_ATTR)) showPanel(chip);
  chip.setAttribute(CUSTOM_PANEL_MARKER_ATTR, "true");
  document.body.appendChild(chip);
}

function measureActionChipHeight(chip: HTMLElement): number {
  return Math.max(chip.scrollHeight, chip.offsetHeight, 380);
}

function positionActionChip(chip: HTMLElement, anchor: HTMLElement | null): void {
  const margin = 12;
  const width = Math.min(400, window.innerWidth - margin * 2);
  chip.style.width = `${width}px`;

  const height = measureActionChipHeight(chip);
  let left = Math.round((window.innerWidth - width) / 2);
  let top = Math.round(window.innerHeight * 0.12);

  if (anchor?.isConnected) {
    const rect = anchor.getBoundingClientRect();
    left = Math.round(rect.right - width);
    top = Math.round(rect.bottom + 8);
    if (left < margin) left = margin;
    if (left + width > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - width - margin);
    }
    if (top + height > window.innerHeight - margin) {
      top = Math.round(rect.top - height - 8);
    }
  }

  top = Math.min(top, window.innerHeight - margin - height);
  top = Math.max(margin, top);

  chip.style.left = `${left}px`;
  chip.style.top = `${top}px`;
}

function ensureActionChipFullyVisible(): void {
  const chip = document.getElementById(FOCUS_ACTION_CHIP_ID);
  if (!(chip instanceof HTMLElement)) return;

  positionActionChip(chip, actionChipAnchor);

  requestAnimationFrame(() => {
    positionActionChip(chip, actionChipAnchor);

    const exp =
      chip.querySelector<HTMLElement>(".sfe-date-picker-input") ??
      chip.querySelector<HTMLElement>('[name="exp_date"]');
    if (!exp) return;

    const margin = 12;
    const expRect = exp.getBoundingClientRect();
    if (expRect.bottom <= window.innerHeight - margin && expRect.top >= margin) return;

    const chipTop = Number.parseFloat(chip.style.top);
    const baseTop = Number.isFinite(chipTop) ? chipTop : chip.getBoundingClientRect().top;
    const shift =
      expRect.bottom > window.innerHeight - margin
        ? expRect.bottom - (window.innerHeight - margin)
        : margin - expRect.top;
    chip.style.top = `${Math.max(margin, baseTop - shift)}px`;
  });
}

function openActionChip(): void {
  const chip = document.getElementById(FOCUS_ACTION_CHIP_ID);
  if (!(chip instanceof HTMLElement)) return;
  actionChipOpen = true;
  actionChipSuppressUntil = Date.now() + 400;
  prepareActionChip(chip);
  chip.classList.add("sfe-focus-action-chip-open");
  positionActionChip(chip, actionChipAnchor);
  watchActionChipKeepAlive();
  requestAnimationFrame(() => ensureActionChipFullyVisible());
}

function closeActionChip(): void {
  const chip = document.getElementById(FOCUS_ACTION_CHIP_ID);
  chip?.classList.remove("sfe-focus-action-chip-open");
  actionChipOpen = false;
  actionChipAnchor = null;
  actionChipEditingUntil = 0;
  stopActionChipKeepAlive();
}

function isFocusActionChipNode(node: Node): boolean {
  if (!(node instanceof HTMLElement)) return false;
  return (
    node.id === FOCUS_ACTION_CHIP_ID ||
    Boolean(node.closest(`#${FOCUS_ACTION_CHIP_ID}`))
  );
}

function watchActionChipKeepAlive(): void {
  actionChipKeepAliveObserver?.disconnect();
  if (!actionChipOpen) return;
  actionChipKeepAliveObserver = new MutationObserver(() => {
    if (!actionChipOpen) return;
    const chip = document.getElementById(FOCUS_ACTION_CHIP_ID);
    if (!(chip instanceof HTMLElement)) return;
    if (!chip.classList.contains("sfe-focus-action-chip-open")) return;
    const hiddenByNative = chip.hasAttribute(NATIVE_PANEL_ATTR);
    const needsMount = !chip.isConnected || chip.parentElement !== document.body;
    if (!hiddenByNative && !needsMount) return;
    prepareActionChip(chip);
    chip.classList.add("sfe-focus-action-chip-open");
    positionActionChip(chip, actionChipAnchor);
  });
  actionChipKeepAliveObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function stopActionChipKeepAlive(): void {
  actionChipKeepAliveObserver?.disconnect();
  actionChipKeepAliveObserver = null;
}

/** Closes Focus action UI (floating chip). Kept name for callers. */
export function closeFocusModals(): void {
  closeActionChip();
}

function ensureActionChipShell(titleId: string, bodyHtml: string): HTMLElement {
  let chip = document.getElementById(FOCUS_ACTION_CHIP_ID);
  if (!chip) {
    chip = document.createElement("div");
    chip.id = FOCUS_ACTION_CHIP_ID;
    chip.className = "sfe-focus-action-chip";
    chip.setAttribute("role", "dialog");
    chip.setAttribute("aria-modal", "false");
    chip.setAttribute("data-sfe-injected", "true");
    chip.setAttribute(CUSTOM_PANEL_MARKER_ATTR, "true");
    chip.innerHTML = `
      <div class="sfe-focus-action-chip-header">
        <h3 id="${titleId}"></h3>
        <button type="button" class="sfe-focus-action-chip-close" data-focus-action-close aria-label="Close">×</button>
      </div>
      <div class="sfe-focus-action-body"></div>`;
    chip.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-focus-action-close]")) {
        e.preventDefault();
        e.stopPropagation();
        closeActionChip();
      }
    });
    chip.addEventListener(
      "focusin",
      (e) => {
        const target = e.target;
        if (target instanceof HTMLElement && target.closest("input, select, textarea")) {
          actionChipEditingUntil = Date.now() + 120_000;
        }
      },
      true,
    );
    chip.addEventListener("focusout", (e) => {
      window.setTimeout(() => {
        if (!actionChipOpen) return;
        const chipEl = document.getElementById(FOCUS_ACTION_CHIP_ID);
        if (isActionChipFormFocused(chipEl)) {
          actionChipEditingUntil = Date.now() + 120_000;
          return;
        }
        if (e.relatedTarget instanceof HTMLElement && chipEl?.contains(e.relatedTarget)) {
          actionChipEditingUntil = Date.now() + 120_000;
          return;
        }
        actionChipEditingUntil = Date.now() + 800;
      }, 0);
    });
    prepareActionChip(chip);
  } else {
    const title = chip.querySelector("h3");
    if (title) title.id = titleId;
    chip.setAttribute("aria-labelledby", titleId);
  }

  const body = chip.querySelector(".sfe-focus-action-body");
  if (body) body.innerHTML = bodyHtml;
  return chip;
}

function buildAddRecSubheader(rec: WheelRecommendation): { text: string; title: string } {
  const ticker = rec.underlying_ticker;
  const stock = stockForTicker(ticker);
  const profit = recommendationProfitability(rec, stock);
  const text = [
    `Mid ${fmtUsd(rec.mid ?? rec.last_price)}`,
    `Profit ${fmtProfitPct(profit.pct)}`,
    `Earn ${fmtEarningsDate(rec.next_earnings_date)}`,
    `Score ${scoreForTicker(ticker, null)}`,
  ].join(" · ");
  return { text, title: profit.tip };
}

function isActionChipFormControl(target: HTMLElement): boolean {
  return Boolean(
    target.closest(
      ".sfe-date-picker, input:not([type='hidden']), select, textarea, button, label, .sfe-focus-action-chip-actions",
    ),
  );
}

function isActionChipFormFocused(chip: HTMLElement | null): boolean {
  const active = document.activeElement;
  if (!(active instanceof HTMLElement) || !chip?.contains(active)) return false;
  return (
    active instanceof HTMLInputElement ||
    active instanceof HTMLSelectElement ||
    active instanceof HTMLTextAreaElement
  );
}

function positionFromPayload(raw: string | undefined): OptionPosition | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as OptionPosition;
  } catch {
    return null;
  }
}

function recFromPayload(raw: string | undefined): WheelRecommendation | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as WheelRecommendation;
  } catch {
    return null;
  }
}

function payloadFromPop(pop: HTMLElement, key: "focusPosition" | "focusRec"): string | undefined {
  const attr = key === "focusPosition" ? "data-focus-position" : "data-focus-rec";
  return pop.getAttribute(attr) ?? undefined;
}

function encodePayload(value: unknown): string {
  return esc(JSON.stringify(value));
}

export function renderFocusPositionRowMenu(position: OptionPosition): string {
  const payload = encodePayload(position);
  return `
    <div class="sfe-focus-row-menu" data-focus-menu="position" data-focus-position="${payload}">
      <button type="button" class="sfe-focus-menu-btn" aria-label="Position actions" aria-haspopup="menu" aria-expanded="false" title="Actions">&#8942;</button>
    </div>`;
}

export function renderFocusRecRowMenu(rec: WheelRecommendation): string {
  const payload = encodePayload(rec);
  return `
    <div class="sfe-focus-row-menu" data-focus-menu="rec" data-focus-rec="${payload}">
      <button type="button" class="sfe-focus-menu-btn" aria-label="Recommendation actions" aria-haspopup="menu" aria-expanded="false" title="Actions">&#8942;</button>
    </div>`;
}

export function renderFocusStockRowMenu(ticker: string): string {
  return `
    <div class="sfe-focus-row-menu" data-focus-menu="stock" data-focus-stock="${esc(ticker)}">
      <button type="button" class="sfe-focus-menu-btn" aria-label="Stock actions" aria-haspopup="menu" aria-expanded="false" title="Actions">&#8942;</button>
    </div>`;
}

function historyFromPayload(raw: string | undefined): HistoryRowMenuPayload | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as HistoryRowMenuPayload;
    if (!(parsed.txnId > 0)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function renderFocusHistoryRowMenu(payload: HistoryRowMenuPayload): string {
  const data = encodePayload(payload);
  return `
    <div class="sfe-focus-row-menu" data-focus-menu="history" data-focus-history="${data}">
      <button type="button" class="sfe-focus-menu-btn" aria-label="Transaction actions" aria-haspopup="menu" aria-expanded="false" title="Actions">&#8942;</button>
    </div>`;
}

function historyMenuHtml(payload: HistoryRowMenuPayload): string {
  return joinMenuItems(
    renderRowFilterMenuItem({
      active: false,
      actionAttr: "data-action",
      actionValue: "focus-wheel-filter",
    }),
    openLegendMenuItem(),
    `<button type="button" class="sfe-focus-menu-item" role="menuitem" data-action="focus-hist-add">Add</button>`,
    payload.linked
      ? `<button type="button" class="sfe-focus-menu-item" role="menuitem" data-action="focus-hist-toggle-linked">${payload.linkedExpanded ? "Hide linked legs" : "Show linked legs"}</button>`
      : "",
    payload.callAssign
      ? `<button type="button" class="sfe-focus-menu-item" role="menuitem" data-action="focus-hist-link-wheel">Link put assign…</button>`
      : "",
    payload.canReassignAccount
      ? `<button type="button" class="sfe-focus-menu-item" role="menuitem" data-action="focus-hist-change-account">Change account…</button>`
      : "",
    `<button type="button" class="sfe-focus-menu-item" role="menuitem" data-action="focus-hist-edit">Edit…</button>`,
    `<button type="button" class="sfe-focus-menu-item sfe-focus-menu-item-danger" role="menuitem" data-action="focus-hist-delete">Delete…</button>`,
  );
}

function restoreKeyFromHost(host: HTMLElement): FocusMenuRestore | null {
  const menuKind = host.dataset.focusMenu;
  if (menuKind === "stock") {
    const stockTicker = host.dataset.focusStock?.trim();
    if (!stockTicker) return null;
    return { ticker: stockTicker, menuKind: "stock" };
  }
  if (menuKind === "history") {
    const payload = historyFromPayload(host.dataset.focusHistory);
    if (!payload) return null;
    const row = host.closest<HTMLElement>("tr[data-txn-id]");
    const ticker =
      row?.querySelector<HTMLElement>(".recs-ticker")?.dataset.ticker?.trim() ??
      row?.querySelector<HTMLElement>("[data-col='ticker']")?.textContent?.trim().split(/\s/)[0] ??
      "";
    return { ticker, menuKind: "history", txnId: payload.txnId };
  }

  const row = host.closest<HTMLElement>(
    ".sfe-watchlist-row, .sfe-calls-plus-row, .sfe-calls-plus-row-group, tr.sfe-stocks-row",
  );
  const ticker = row?.dataset.ticker;
  if (!ticker) return null;

  if (menuKind === "position") {
    const position = positionFromPayload(host.dataset.focusPosition);
    if (!position?.option_symbol) return null;
    return { ticker, menuKind: "position", positionSymbol: position.option_symbol };
  }
  if (menuKind === "rec") {
    const rec = recFromPayload(host.dataset.focusRec);
    if (!rec) return null;
    return {
      ticker,
      menuKind: "rec",
      recKey: `${rec.side}|${rec.strike}|${rec.expiration_date ?? ""}`,
    };
  }
  return null;
}

function findMenuButton(restore: FocusMenuRestore): HTMLElement | null {
  if (restore.menuKind === "history" && restore.txnId) {
    const row = document.querySelector<HTMLElement>(
      `#wheelHistoryTable tr[data-txn-id="${restore.txnId}"]`,
    );
    if (!row) return null;
    for (const host of row.querySelectorAll<HTMLElement>(".sfe-focus-row-menu")) {
      if (host.dataset.focusMenu !== "history") continue;
      const payload = historyFromPayload(host.dataset.focusHistory);
      if (payload?.txnId !== restore.txnId) continue;
      return host.querySelector<HTMLElement>(".sfe-focus-menu-btn");
    }
    return null;
  }

  const row =
    document.querySelector<HTMLElement>(
      `.sfe-watchlist-row[data-ticker="${CSS.escape(restore.ticker)}"]`,
    ) ??
    document.querySelector<HTMLElement>(
      `.sfe-calls-plus-row[data-ticker="${CSS.escape(restore.ticker)}"], .sfe-calls-plus-row-group[data-ticker="${CSS.escape(restore.ticker)}"] .sfe-calls-plus-row`,
    ) ??
    document.querySelector<HTMLElement>(
      `tr.sfe-stocks-row[data-ticker="${CSS.escape(restore.ticker)}"]`,
    );
  if (!row) return null;

  for (const host of row.querySelectorAll<HTMLElement>(".sfe-focus-row-menu")) {
    if (host.dataset.focusMenu !== restore.menuKind) continue;
    if (restore.menuKind === "stock") {
      if (host.dataset.focusStock?.trim().toUpperCase() !== restore.ticker.trim().toUpperCase()) {
        continue;
      }
      return host.querySelector<HTMLElement>(".sfe-focus-menu-btn");
    }
    if (restore.menuKind === "position") {
      const position = positionFromPayload(host.dataset.focusPosition);
      if (position?.option_symbol !== restore.positionSymbol) continue;
    } else {
      const rec = recFromPayload(host.dataset.focusRec);
      const recKey = rec
        ? `${rec.side}|${rec.strike}|${rec.expiration_date ?? ""}`
        : "";
      if (recKey !== restore.recKey) continue;
    }
    return host.querySelector<HTMLElement>(".sfe-focus-menu-btn");
  }
  return null;
}

function isFocusMenuPortalNode(node: Node): boolean {
  if (!(node instanceof HTMLElement)) return false;
  return node.id === FOCUS_MENU_PORTAL_ID || Boolean(node.closest(`#${FOCUS_MENU_PORTAL_ID}`));
}

function isFocusExtensionUiNode(node: Node): boolean {
  return isFocusMenuPortalNode(node) || isFocusActionChipNode(node) || isToastChipNode(node);
}

export function focusMenuPortalMutation(mutations: MutationRecord[]): boolean {
  return focusExtensionUiMutation(mutations);
}

export function focusExtensionUiMutation(mutations: MutationRecord[]): boolean {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (isFocusExtensionUiNode(node)) return true;
    }
    for (const node of mutation.removedNodes) {
      if (isFocusExtensionUiNode(node)) return true;
    }
  }
  return false;
}

function mountMenuPortal(portal: HTMLElement): void {
  (document.body ?? document.documentElement).appendChild(portal);
}

function watchMenuPortalKeepAlive(): void {
  menuKeepAliveObserver?.disconnect();
  menuKeepAliveObserver = new MutationObserver(() => {
    const portal = document.getElementById(FOCUS_MENU_PORTAL_ID);
    if (!openMenuRestore || portal?.isConnected) return;
    const btn = findMenuButton(openMenuRestore);
    if (!btn) {
      closeFocusRowMenus();
      return;
    }
    openMenuAnchor = btn;
    const restored = ensureMenuPortal();
    restored.classList.remove("hidden");
    positionMenuPortal(btn, restored);
    btn.setAttribute("aria-expanded", "true");
    mountMenuPortal(restored);
  });
  menuKeepAliveObserver.observe(document.documentElement, { childList: true, subtree: true });
}

function stopMenuPortalKeepAlive(): void {
  menuKeepAliveObserver?.disconnect();
  menuKeepAliveObserver = null;
}

function ensureMenuPortal(): HTMLElement {
  let portal = document.getElementById(FOCUS_MENU_PORTAL_ID);
  if (!portal) {
    portal = document.createElement("div");
    portal.id = FOCUS_MENU_PORTAL_ID;
    portal.className = "sfe-focus-menu-pop hidden";
    portal.setAttribute("role", "menu");
    portal.setAttribute("data-sfe-injected", "true");
    portal.setAttribute(CUSTOM_PANEL_MARKER_ATTR, "true");
  }
  return portal;
}

export function closeFocusRowMenus(): void {
  const portal = document.getElementById(FOCUS_MENU_PORTAL_ID);
  portal?.classList.add("hidden");
  openMenuAnchor?.setAttribute("aria-expanded", "false");
  openMenuAnchor = null;
  openMenuRestore = null;
  stopMenuPortalKeepAlive();
  document.querySelectorAll(".sfe-focus-menu-btn[aria-expanded='true']").forEach((btn) => {
    btn.setAttribute("aria-expanded", "false");
  });
}

function cssLayoutZoom(): number {
  const raw = getComputedStyle(document.documentElement).zoom;
  const z = parseFloat(String(raw || "1"));
  return Number.isFinite(z) && z > 0 ? z : 1;
}

function viewportBox(): { left: number; top: number; width: number; height: number } {
  if (isStockForgeDesktop()) {
    const zoom = cssLayoutZoom();
    return {
      left: 0,
      top: 0,
      width: (window.innerWidth || document.documentElement.clientWidth || 0) / zoom,
      height: (window.innerHeight || document.documentElement.clientHeight || 0) / zoom,
    };
  }
  const vv = window.visualViewport;
  if (vv) {
    return { left: vv.offsetLeft, top: vv.offsetTop, width: vv.width, height: vv.height };
  }
  return {
    left: 0,
    top: 0,
    width: document.documentElement.clientWidth || window.innerWidth,
    height: document.documentElement.clientHeight || window.innerHeight,
  };
}

function positionMenuPortal(btn: HTMLElement, portal: HTMLElement): void {
  if (!portal.isConnected) mountMenuPortal(portal);
  portal.classList.remove("hidden");
  portal.style.visibility = "hidden";
  portal.style.left = "0";
  portal.style.top = "0";
  const pad = 8;
  const zoom = isStockForgeDesktop() ? cssLayoutZoom() : 1;
  const view = viewportBox();
  portal.style.maxWidth = `${Math.max(120, view.width - pad * 2)}px`;
  const pw = portal.offsetWidth;
  const ph = portal.offsetHeight;
  const r = btn.getBoundingClientRect();
  let left = Math.round(r.right / zoom - pw);
  let top = Math.round(r.bottom / zoom + 4);
  const minLeft = view.left + pad;
  const maxLeft = view.left + view.width - pw - pad;
  left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));
  if (top + ph > view.top + view.height - pad && r.top / zoom - view.top > ph + pad) {
    top = Math.round(r.top / zoom - ph - 4);
  }
  const minTop = view.top + pad;
  const maxTop = view.top + view.height - ph - pad;
  top = Math.min(Math.max(top, minTop), Math.max(minTop, maxTop));
  portal.style.left = `${left}px`;
  portal.style.top = `${top}px`;
  portal.style.visibility = "";
}

function attachWheelFilterContext(portal: HTMLElement, host: HTMLElement, menuKind: string): void {
  portal.removeAttribute("data-wheel-filter-ticker");
  portal.removeAttribute("data-wheel-filter-target");

  if (menuKind === "position") {
    const position = positionFromPayload(host.dataset.focusPosition);
    const ticker = position?.ticker?.trim();
    if (!ticker) return;
    portal.setAttribute("data-wheel-filter-ticker", ticker);
    portal.setAttribute("data-wheel-filter-target", "options");
    return;
  }

  if (menuKind === "rec") {
    const rec = recFromPayload(host.dataset.focusRec);
    const ticker = rec?.underlying_ticker?.trim();
    if (!ticker || !rec) return;
    portal.setAttribute("data-wheel-filter-ticker", ticker);
    portal.setAttribute("data-wheel-filter-target", wheelTargetForRecSide(rec.side));
    return;
  }

  if (menuKind === "stock") {
    const ticker = host.dataset.focusStock?.trim();
    if (!ticker) return;
    portal.setAttribute("data-wheel-filter-ticker", ticker);
    portal.setAttribute("data-wheel-filter-target", "stocks");
    return;
  }

  if (menuKind === "history") {
    const row = host.closest("tr");
    const ticker =
      row?.querySelector<HTMLElement>(".recs-ticker")?.dataset.ticker?.trim() ??
      row?.querySelector<HTMLElement>("[data-col='ticker']")?.textContent?.trim().split(/\s/)[0] ??
      "";
    if (!ticker) return;
    portal.setAttribute("data-wheel-filter-ticker", ticker);
    portal.setAttribute("data-wheel-filter-target", "history");
  }
}

function paintWheelFilterMenuItem(portal: HTMLElement): void {
  const ticker = portal.getAttribute("data-wheel-filter-ticker")?.trim();
  const target = portal.getAttribute("data-wheel-filter-target") as WheelTickerFilterTarget | null;
  if (!ticker || !target) return;
  const existing = portal.querySelector<HTMLElement>('[data-action="focus-wheel-filter"]');
  const html = renderRowFilterMenuItem({
    active: isWheelTabTickerFilterActive(target, ticker),
    actionAttr: "data-action",
    actionValue: "focus-wheel-filter",
  });
  if (existing) existing.outerHTML = html;
  else portal.insertAdjacentHTML("afterbegin", html);
}

function tickerFromPortal(portal: HTMLElement): string {
  const fromFilter = portal.getAttribute("data-wheel-filter-ticker")?.trim();
  if (fromFilter) return fromFilter;
  const stock = portal.getAttribute("data-focus-stock")?.trim();
  if (stock) return stock;
  const rec = recFromPayload(payloadFromPop(portal, "focusRec"));
  if (rec?.underlying_ticker?.trim()) return rec.underlying_ticker.trim();
  const position = positionFromPayload(payloadFromPop(portal, "focusPosition"));
  return position?.ticker?.trim() || "";
}

function legendKindFromPortal(portal: HTMLElement): LegendOpenContext {
  const target = portal.getAttribute("data-wheel-filter-target");
  if (target === "puts" || target === "calls" || target === "options" || target === "stocks" || target === "history") {
    return target;
  }
  if (portal.getAttribute("data-focus-stock")) return "stocks";
  if (portal.getAttribute("data-focus-position")) return "options";
  if (portal.getAttribute("data-focus-history")) return "history";
  return "puts";
}

function robinhoodIntentFromPortal(portal: HTMLElement) {
  const rec = recFromPayload(payloadFromPop(portal, "focusRec"));
  const position = positionFromPayload(payloadFromPop(portal, "focusPosition"));
  const stockTicker = portal.getAttribute("data-focus-stock")?.trim() || "";
  if (stockTicker) return { kind: "stock" as const, ticker: stockTicker };
  const filterTarget = portal.getAttribute("data-wheel-filter-target");
  const tableKind = filterTarget === "calls" || filterTarget === "puts" ? filterTarget : undefined;
  return rec
    ? robinhoodIntentFromRec(rec, tableKind)
    : robinhoodIntentFromPosition(position);
}

function paintRobinhoodMenuLink(portal: HTMLElement): void {
  const link = portal.querySelector<HTMLAnchorElement>("[data-action='focus-open-robinhood']");
  if (!link) return;
  const intent = robinhoodIntentFromPortal(portal);
  const url = intent ? robinhoodHrefForIntent(intent) : null;
  if (!url) {
    link.removeAttribute("href");
    return;
  }
  link.href = url;
  link.target = ROBINHOOD_WINDOW_NAME;
  link.rel = "opener";
  prefetchRobinhoodPage(url);
  if (!intent || intent.kind === "stock" || intent.optionId) return;
  void resolveRobinhoodHref(intent).then((resolved) => {
    if (!resolved || !link.isConnected) return;
    if (link.dataset.action !== "focus-open-robinhood") return;
    link.href = resolved;
    prefetchRobinhoodPage(resolved);
  });
}

function openFocusRowMenu(btn: HTMLElement): void {
  const host = btn.closest<HTMLElement>(".sfe-focus-row-menu");
  if (!host) return;

  const portal = ensureMenuPortal();
  const menuKind = host.dataset.focusMenu;
  if (menuKind === "position") {
    portal.innerHTML = positionMenuHtml();
    const payload = host.dataset.focusPosition;
    if (payload) portal.setAttribute("data-focus-position", payload);
    portal.removeAttribute("data-focus-rec");
  } else if (menuKind === "rec") {
    portal.innerHTML = recMenuHtml();
    const payload = host.dataset.focusRec;
    if (payload) portal.setAttribute("data-focus-rec", payload);
    portal.removeAttribute("data-focus-position");
    portal.removeAttribute("data-focus-stock");
  } else if (menuKind === "stock") {
    portal.innerHTML = stockMenuHtml();
    const ticker = host.dataset.focusStock;
    if (ticker) portal.setAttribute("data-focus-stock", ticker);
    portal.removeAttribute("data-focus-position");
    portal.removeAttribute("data-focus-rec");
    portal.removeAttribute("data-focus-history");
  } else if (menuKind === "history") {
    const payload = historyFromPayload(host.dataset.focusHistory);
    if (!payload) return;
    portal.innerHTML = historyMenuHtml(payload);
    portal.setAttribute("data-focus-history", host.dataset.focusHistory ?? "");
    portal.removeAttribute("data-focus-position");
    portal.removeAttribute("data-focus-rec");
    portal.removeAttribute("data-focus-stock");
  } else {
    return;
  }

  attachWheelFilterContext(portal, host, menuKind);
  paintWheelFilterMenuItem(portal);
  paintRobinhoodMenuLink(portal);

  openMenuRestore = restoreKeyFromHost(host);
  mountMenuPortal(portal);
  positionMenuPortal(btn, portal);
  openMenuAnchor = btn;
  btn.setAttribute("aria-expanded", "true");
  watchMenuPortalKeepAlive();
}

function requestOpenFocusRowMenu(btn: HTMLElement): void {
  menuSuppressCloseUntil = Date.now() + 500;
  if (!btn.isConnected) return;
  const portal = document.getElementById(FOCUS_MENU_PORTAL_ID);
  const alreadyOpen = openMenuAnchor === btn && portal && !portal.classList.contains("hidden");
  if (alreadyOpen) {
    // Electron focuses the BrowserView and can fire a second pointerdown on the
    // same click. Treat that as the same gesture instead of toggling closed.
    if (Date.now() - menuOpenedAt < MENU_GESTURE_MS) return;
    closeFocusRowMenus();
    return;
  }
  closeFocusRowMenus();
  openFocusRowMenu(btn);
  menuOpenedAt = Date.now();
}

export function syncFocusRowMenuAfterDomUpdate(): void {
  const portal = document.getElementById(FOCUS_MENU_PORTAL_ID);
  if (!portal || portal.classList.contains("hidden") || !openMenuRestore) return;

  if (openMenuAnchor?.isConnected) {
    positionMenuPortal(openMenuAnchor, portal);
    mountMenuPortal(portal);
    return;
  }

  const btn = findMenuButton(openMenuRestore);
  if (!btn) {
    closeFocusRowMenus();
    return;
  }

  openMenuAnchor = btn;
  btn.setAttribute("aria-expanded", "true");
  positionMenuPortal(btn, portal);
  mountMenuPortal(portal);
}

export function bindFocusRowMenuDocumentHandlers(): void {
  if (rowMenuDocBound) return;
  rowMenuDocBound = true;

  if (!focusActionChipEscapeBound) {
    focusActionChipEscapeBound = true;
    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape" || !actionChipOpen) return;
      e.preventDefault();
      e.stopPropagation();
      closeActionChip();
    });
  }

  document.addEventListener(
    "pointerdown",
    (e) => {
      const target = e.target as HTMLElement;
      const menuBtn = target.closest<HTMLElement>(".sfe-focus-menu-btn");
      if (!menuBtn) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      requestOpenFocusRowMenu(menuBtn);
    },
    true,
  );

  document.addEventListener(
    "click",
    (e) => {
      if (Date.now() < menuSuppressCloseUntil) return;

      const target = e.target as HTMLElement;
      const portal = document.getElementById(FOCUS_MENU_PORTAL_ID);
      const actionChip = document.getElementById(FOCUS_ACTION_CHIP_ID);

      const item = target.closest<HTMLElement>(".sfe-focus-menu-item");
      if (item && portal?.contains(item)) {
        actionChipSuppressUntil = Date.now() + 400;
        actionChipAnchor = openMenuAnchor;
        if (item.dataset.action === "focus-open-robinhood") {
          const href = item instanceof HTMLAnchorElement ? item.href : "";
          if (!consumeRobinhoodMenuClick(e, href) && e.defaultPrevented) {
            showToast("Could not open Robinhood for this ticker.", "error");
          }
          window.setTimeout(() => closeFocusRowMenus(), 0);
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        const actionItem = item;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            void handleFocusMenuItem(actionItem);
          });
        });
        return;
      }

      if (actionChip?.contains(target)) {
        if (target.closest("[data-focus-action-close]")) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          closeActionChip();
          return;
        }
        if (isActionChipFormControl(target)) {
          return;
        }
        e.stopPropagation();
        return;
      }

      if (
        target.closest(".sfe-focus-menu-btn") ||
        target.closest(".sfe-focus-row-menu") ||
        target.closest(`#${FOCUS_MENU_PORTAL_ID}`)
      ) {
        e.stopPropagation();
        return;
      }

      closeFocusRowMenus();
      if (
        actionChipOpen &&
        Date.now() >= actionChipSuppressUntil &&
        Date.now() >= actionChipEditingUntil &&
        !isActionChipFormFocused(actionChip)
      ) {
        closeActionChip();
      }
    },
    true,
  );

  window.addEventListener("resize", () => {
    const portal = document.getElementById(FOCUS_MENU_PORTAL_ID);
    if (
      portal &&
      !portal.classList.contains("hidden") &&
      openMenuAnchor?.isConnected
    ) {
      positionMenuPortal(openMenuAnchor, portal);
    }
    const chip = document.getElementById(FOCUS_ACTION_CHIP_ID);
    if (chip?.classList.contains("sfe-focus-action-chip-open")) {
      positionActionChip(chip, actionChipAnchor);
    }
  });
}

function recFromPosition(position: OptionPosition): WheelRecommendation {
  return {
    underlying_ticker: position.ticker,
    side: position.side || "put",
    strike: Number(position.strike),
    expiration_date: position.exp_date,
    mid: position.current_price,
    last_price: position.entry_price,
  };
}

async function handleFocusMenuItem(item: HTMLElement): Promise<void> {
  const pop = document.getElementById(FOCUS_MENU_PORTAL_ID);
  const action = item.dataset.action;
  closeFocusRowMenus();
  if (!action || !pop) return;

  if (action === "focus-wheel-filter") {
    const ticker = pop.getAttribute("data-wheel-filter-ticker")?.trim();
    const target = pop.getAttribute("data-wheel-filter-target") as WheelTickerFilterTarget | null;
    if (ticker && target) toggleWheelTabTickerFilter(target, ticker);
    return;
  }

  if (action === "focus-open-robinhood") {
    const intent = robinhoodIntentFromPortal(pop);
    const opened = intent
      ? openRobinhoodNearbyTab(robinhoodHrefForIntent(intent) ?? "")
      : false;
    if (!opened) {
      showToast("Could not open Robinhood for this ticker.", "error");
    }
    return;
  }

  if (action === "focus-open-legend") {
    if (!openLegendForContext(legendKindFromPortal(pop), tickerFromPortal(pop))) {
      showToast("Could not open Legend for this ticker.", "error");
    }
    return;
  }

  if (action === "focus-rec-add") {
    const rec = recFromPayload(payloadFromPop(pop, "focusRec"));
    if (rec) openAddRecModal(rec);
    return;
  }

  if (action === "focus-stock-history") {
    const ticker = pop.getAttribute("data-focus-stock")?.trim();
    if (ticker) {
      document.dispatchEvent(
        new CustomEvent(STOCK_HISTORY_OPEN_EVENT, { detail: { ticker } }),
      );
    }
    return;
  }

  if (
    action === "focus-hist-add" ||
    action === "focus-hist-edit" ||
    action === "focus-hist-delete" ||
    action === "focus-hist-toggle-linked" ||
    action === "focus-hist-link-wheel" ||
    action === "focus-hist-change-account"
  ) {
    const payload = historyFromPayload(pop.getAttribute("data-focus-history") ?? undefined);
    if (!payload) return;
    const menuAction: HistoryTxnMenuAction =
      action === "focus-hist-add"
        ? "add"
        : action === "focus-hist-edit"
          ? "edit"
          : action === "focus-hist-delete"
            ? "delete"
            : action === "focus-hist-link-wheel"
              ? "link-wheel-pair"
              : action === "focus-hist-change-account"
                ? "change-account"
                : "toggle-linked";
    document.dispatchEvent(
      new CustomEvent(HISTORY_TXN_MENU_EVENT, {
        detail: { action: menuAction, ...payload },
      }),
    );
    return;
  }

  const position = positionFromPayload(payloadFromPop(pop, "focusPosition"));
  if (!position) return;

  if (action === "focus-pos-add") {
    openAddRecModal(recFromPosition(position));
    return;
  }
  if (action === "focus-pos-edit") {
    await openEditPositionModal(position);
    return;
  }
  if (action === "focus-pos-close") {
    openResolveModal(position, "closed");
    return;
  }
  if (action === "focus-pos-expired") {
    openResolveModal(position, "expired");
    return;
  }
  if (action === "focus-pos-assigned") {
    openResolveModal(position, "assigned");
    return;
  }
  if (action === "focus-pos-delete") {
    await confirmDeletePosition(position);
  }
}

function openResolveModal(position: OptionPosition, resolution: PositionResolution): void {
  const meta = RESOLVE_META[resolution];
  const chip = ensureActionChipShell(
    "sfe-focus-resolve-title",
    `
    <p class="sfe-focus-action-sub" data-resolve-sub></p>
    <p class="sfe-focus-action-hint" data-resolve-hint></p>
    <form class="sfe-focus-action-form" data-resolve-form>
      <label class="sfe-modal-field">Trade date
        <input type="date" name="trade_date" required />
      </label>
      <label class="sfe-modal-field">Contracts <span class="sfe-focus-action-optional">(blank = all open)</span>
        <input type="number" name="quantity" min="1" step="1" />
      </label>
      <label class="sfe-modal-field sfe-focus-resolve-price" data-resolve-price-wrap>BTC premium per share
        <input type="number" name="avg_price" min="0" step="0.01" />
      </label>
      <div class="sfe-focus-action-chip-actions">
        <button type="button" class="sfe-btn" data-focus-action-close>Cancel</button>
        <button type="submit" class="sfe-btn sfe-btn-primary" data-resolve-submit>${esc(meta.submit)}</button>
      </div>
    </form>`,
  );

  chip.querySelector("#sfe-focus-resolve-title")!.textContent =
    `${meta.title} · ${positionSummary(position)}`;
  chip.querySelector("[data-resolve-sub]")!.textContent =
    `Entry ${fmtUsd(position.entry_price)} · mid ${fmtUsd(position.current_price)}`;
  chip.querySelector("[data-resolve-hint]")!.textContent = meta.hint;
  const priceWrap = chip.querySelector<HTMLElement>("[data-resolve-price-wrap]");
  if (priceWrap) priceWrap.hidden = !meta.showPrice;

  const form = chip.querySelector<HTMLFormElement>("[data-resolve-form]")!;
  const tradeDate = form.elements.namedItem("trade_date") as HTMLInputElement;
  const qty = form.elements.namedItem("quantity") as HTMLInputElement;
  const price = form.elements.namedItem("avg_price") as HTMLInputElement;
  tradeDate.value = etTodayISO();
  qty.value = "";
  qty.max = String(position.contracts ?? "");
  price.value = position.current_price != null ? String(position.current_price) : "";
  price.required = meta.showPrice;

  form.onsubmit = async (e) => {
    e.preventDefault();
    const submit = form.querySelector<HTMLButtonElement>("[data-resolve-submit]");
    if (submit) submit.disabled = true;
    try {
      const qtyRaw = qty.value.trim();
      const qtyVal = qtyRaw ? Number(qtyRaw) : undefined;
      if (qtyVal != null && (!(qtyVal > 0) || qtyVal > (position.contracts ?? 0))) {
        throw new WheelActionError(
          `Contracts must be between 1 and ${fmtNum(position.contracts, 0)}.`,
        );
      }
      await resolveOpenPosition(position, resolution, {
        tradeDate: tradeDate.value,
        quantity: qtyVal,
        avgPrice: meta.showPrice ? Number(price.value) : undefined,
      });
      showToast(`${position.ticker} ${position.side ?? ""} marked ${resolution}.`, "success");
      closeActionChip();
      await afterWheelLedgerChange(refreshFn ?? undefined);
    } catch (err) {
      actionErr(err);
    } finally {
      if (submit) submit.disabled = false;
    }
  };

  openActionChip();
}

async function openEditPositionModal(position: OptionPosition): Promise<void> {
  const txn = await findOpeningTransaction(position);
  if (!txn) {
    showToast("No editable opening transaction found for this contract.", "error");
    return;
  }

  const chip = ensureActionChipShell(
    "sfe-focus-edit-title",
    `
    <p class="sfe-focus-action-sub">Editing transaction #${txn.id}</p>
    <form class="sfe-focus-action-form" data-edit-form>
      <label class="sfe-modal-field">Trade date
        <input type="date" name="trade_date" required />
      </label>
      <label class="sfe-modal-field">Contracts
        <input type="number" name="quantity" min="1" step="1" required />
      </label>
      <label class="sfe-modal-field">Premium per share
        <input type="number" name="avg_price" min="0" step="0.01" required />
      </label>
      <label class="sfe-modal-field">Strike
        <input type="number" name="strike" min="0" step="0.01" required />
      </label>
      <label class="sfe-modal-field">Expiration
        <input type="text" name="exp_date" class="sfe-focus-exp-input" placeholder="YYYY-MM-DD" inputmode="numeric" autocomplete="off" spellcheck="false" maxlength="10" required />
      </label>
      <div class="sfe-focus-action-chip-actions">
        <button type="button" class="sfe-btn" data-focus-action-close>Cancel</button>
        <button type="submit" class="sfe-btn sfe-btn-primary">Save changes</button>
      </div>
    </form>`,
  );

  chip.querySelector("#sfe-focus-edit-title")!.textContent =
    `Edit position · ${positionSummary(position)}`;

  const form = chip.querySelector<HTMLFormElement>("[data-edit-form]")!;
  const expInput = form.elements.namedItem("exp_date") as HTMLInputElement;
  (form.elements.namedItem("trade_date") as HTMLInputElement).value =
    txn.trade_date ?? etTodayISO();
  (form.elements.namedItem("quantity") as HTMLInputElement).value = String(txn.quantity ?? "");
  (form.elements.namedItem("avg_price") as HTMLInputElement).value = String(txn.avg_price ?? "");
  (form.elements.namedItem("strike") as HTMLInputElement).value = String(
    txn.strike ?? position.strike ?? "",
  );
  prepareExpDateField(expInput, txn.exp_date ?? position.exp_date, {
    onInteract: () => {
      actionChipEditingUntil = Date.now() + 120_000;
    },
  });

  form.onsubmit = async (e) => {
    e.preventDefault();
    const submit = form.querySelector<HTMLButtonElement>('[type="submit"]');
    if (submit) submit.disabled = true;
    try {
      await updateOpeningTransaction(position, txn, {
        tradeDate: (form.elements.namedItem("trade_date") as HTMLInputElement).value,
        quantity: Number((form.elements.namedItem("quantity") as HTMLInputElement).value),
        avgPrice: Number((form.elements.namedItem("avg_price") as HTMLInputElement).value),
        strike: Number((form.elements.namedItem("strike") as HTMLInputElement).value),
        expDate: readExpDateField(expInput),
      });
      showToast("Transaction updated.", "success");
      closeActionChip();
      await afterWheelLedgerChange(refreshFn ?? undefined);
    } catch (err) {
      actionErr(err);
    } finally {
      if (submit) submit.disabled = false;
    }
  };

  openActionChip();
}

function openAddRecModal(rec: WheelRecommendation): void {
  const ticker = rec.underlying_ticker;
  const side = rec.side || "put";
  const chip = ensureActionChipShell(
    "sfe-focus-add-title",
    `
    <p class="sfe-focus-action-sub" data-add-sub></p>
    <form class="sfe-focus-action-form" data-add-form>
      <label class="sfe-modal-field sfe-focus-trade-date-field">Trade date
        <span class="sfe-focus-field-static" data-trade-date-display></span>
        <input type="hidden" name="trade_date" />
      </label>
      <label class="sfe-modal-field">Strike
        <input type="number" name="strike" min="0" step="0.01" required />
      </label>
      <label class="sfe-modal-field">Contracts
        <input type="number" name="quantity" min="1" step="1" required />
      </label>
      <label class="sfe-modal-field">Premium per share
        <input type="number" name="avg_price" min="0" step="0.01" required />
      </label>
      <label class="sfe-modal-field sfe-focus-expiration-field">Expiration date
        <input type="text" name="exp_date" class="sfe-focus-exp-input" placeholder="YYYY-MM-DD" inputmode="numeric" autocomplete="off" spellcheck="false" maxlength="10" required />
      </label>
      <div class="sfe-focus-action-chip-actions">
        <button type="button" class="sfe-btn" data-focus-action-close>Cancel</button>
        <button type="submit" class="sfe-btn sfe-btn-primary">Add position</button>
      </div>
    </form>`,
  );

  const form = chip.querySelector<HTMLFormElement>("[data-add-form]")!;
  const titleEl = chip.querySelector("#sfe-focus-add-title")!;
  const subEl = chip.querySelector("[data-add-sub]")!;
  const strikeInput = form.elements.namedItem("strike") as HTMLInputElement;
  const expInput = form.elements.namedItem("exp_date") as HTMLInputElement;

  const syncAddRecHeader = (): void => {
    titleEl.textContent = `Add ${ticker} ${side.toUpperCase()} ${fmtNum(Number(strikeInput.value), 2)}`;
  };

  const sub = buildAddRecSubheader(rec);
  subEl.textContent = sub.text;
  subEl.title = sub.title;

  const tradeDate = etTodayISO();
  const tradeDateInput = form.elements.namedItem("trade_date") as HTMLInputElement;
  tradeDateInput.value = tradeDate;
  chip.querySelector("[data-trade-date-display]")!.textContent = tradeDate;
  (form.elements.namedItem("quantity") as HTMLInputElement).value = "1";
  (form.elements.namedItem("avg_price") as HTMLInputElement).value =
    rec.mid != null ? Number(rec.mid).toFixed(2) : rec.last_price != null ? String(rec.last_price) : "";
  strikeInput.value = rec.strike != null ? String(rec.strike) : "";
  prepareExpDateField(expInput, rec.expiration_date, {
    onInteract: () => {
      actionChipEditingUntil = Date.now() + 120_000;
    },
  });
  if (!chip.querySelector(".sfe-date-picker")) {
    showToast("Expiration picker failed to mount. Reload the page.", "error");
  }
  strikeInput.addEventListener("input", syncAddRecHeader);
  syncAddRecHeader();

  form.onsubmit = async (e) => {
    e.preventDefault();
    const submit = form.querySelector<HTMLButtonElement>('[type="submit"]');
    if (submit) submit.disabled = true;
    try {
      await addRecommendationPosition(rec, {
        tradeDate: tradeDateInput.value,
        quantity: Number((form.elements.namedItem("quantity") as HTMLInputElement).value),
        avgPrice: Number((form.elements.namedItem("avg_price") as HTMLInputElement).value),
        strike: Number(strikeInput.value),
        expDate: readExpDateField(expInput),
      });
      showToast("Position added to your portfolio.", "success");
      closeActionChip();
      await afterWheelLedgerChange(refreshFn ?? undefined);
    } catch (err) {
      actionErr(err);
    } finally {
      if (submit) submit.disabled = false;
    }
  };

  openActionChip();
  ensureActionChipFullyVisible();
}

async function confirmDeletePosition(position: OptionPosition): Promise<void> {
  try {
    const txnCount = await countPositionTransactions(position);
    if (!txnCount) {
      showToast("No transactions found for this contract.", "error");
      return;
    }
    const ok = await confirmDialog({
      title: "Delete position",
      message: deleteConfirmMessage(position, txnCount),
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    const deleted = await deleteOpenPosition(position);
    showToast(`Deleted ${deleted} transaction(s) for this contract.`, "success");
    await afterWheelLedgerChange(refreshFn ?? undefined);
  } catch (err) {
    actionErr(err);
  }
}

export function openFocusResolveAction(
  position: OptionPosition,
  resolution: PositionResolution,
): void {
  openResolveModal(position, resolution);
}

export function openFocusEditAction(position: OptionPosition): void {
  void openEditPositionModal(position);
}

export function openFocusAddRecAction(rec: WheelRecommendation): void {
  openAddRecModal(rec);
}

export function openFocusDeleteAction(position: OptionPosition): void {
  void confirmDeletePosition(position);
}
